import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface ProfileData {
  fullName: string | null;
  email: string;
  isLoading: boolean;
  error: string | null;
  updateProfile: (fullName: string) => Promise<boolean>;
}

export function useProfile(): ProfileData {
  const { user } = useAuth();
  const [fullName, setFullName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("user_id", user.id)
        .single();

      if (profileError && profileError.code !== "PGRST116") {
        throw new Error("Erro ao buscar perfil");
      }

      setFullName(profile?.full_name ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro desconhecido");
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = async (newFullName: string): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ full_name: newFullName })
        .eq("user_id", user.id);

      if (updateError) {
        throw new Error("Erro ao atualizar perfil");
      }

      setFullName(newFullName);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro desconhecido");
      return false;
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [user]);

  return {
    fullName,
    email: user?.email ?? "",
    isLoading,
    error,
    updateProfile,
  };
}
